// MUI theme for the Promotions list (Promotions.jsx), the promotion wizard
// (PromotionWizard.jsx) and its help drawer (PromotionHelp.jsx) ONLY.
//
// It is the Truck Stops theme (ink primary, amber accent, orange focus ring, no
// green / blue / indigo / purple, labels above 48px fields) plus what these
// pages have that Truck Stops does not: switches, autocompletes with tag chips,
// input adornments, plain text buttons, a red destructive button and themed
// alerts. Dialogs, menus, autocomplete popups and the help drawer are portaled
// but sit inside the React tree, so they pick this up too.
import { createTheme } from '@mui/material/styles'
import { truckStopsTheme, TS, focusRing } from './truckStopsTheme'

export const PM = {
  ...TS,
  body: '#344054',
  innerBorder: '#C9CED6',
  amberRing: '#FDE9BF',
  amberHover: '#D97706',
  paleAmber: '#FFF8E8',
  noteBorder: '#F0D9A8',
  dashed: '#667085',
  rowBorder: '#EEF0F3',
  headBg: '#F6F7F9',
  ghostHover: '#EEF0F3',
  disabledText: '#667085'
}

export { focusRing }

const extra = base => ({
  MuiOutlinedInput: {
    styleOverrides: {
      ...base.components.MuiOutlinedInput.styleOverrides,
      // The page theme pads every input 0 14px; beside a "$" / "%" adornment
      // the input needs no padding on that side.
      inputAdornedStart: { paddingLeft: 0 },
      inputAdornedEnd: { paddingRight: 0 },
      // Textareas: same border and radius, 12px 14px padding, natural height.
      multiline: { padding: '12px 14px', alignItems: 'flex-start' },
      inputMultiline: { height: 'auto', padding: 0, lineHeight: 1.5 }
    }
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: { '& .MuiTypography-root': { color: TS.secondary, fontWeight: 600 } }
    }
  },
  MuiAutocomplete: {
    styleOverrides: {
      // Keep the 48px field height: 5px + 36px input + 5px + border.
      root: {
        '& .MuiOutlinedInput-root, & .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
          minHeight: 48, paddingTop: 5, paddingBottom: 5, paddingLeft: 8, gap: 6
        },
        '& .MuiOutlinedInput-root .MuiAutocomplete-input, & .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
          height: 36, padding: '0 6px'
        }
      },
      tag: { margin: 0 },
      paper: { borderRadius: 12, border: `1px solid ${TS.border}`, boxShadow: '0 12px 28px rgba(15, 23, 42, 0.14)' },
      option: {
        minHeight: '44px !important',
        fontSize: 15,
        '&[aria-selected="true"]': { backgroundColor: `${TS.chipBg} !important` },
        '&.Mui-focused': { backgroundColor: `${TS.page} !important` }
      }
    }
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 8, fontWeight: 600, color: TS.label, backgroundColor: TS.chipBg },
      deleteIcon: { color: TS.secondary, '&:hover': { color: TS.text } }
    }
  },
  MuiSwitch: {
    styleOverrides: {
      // 44x26 track, 20px knob. Everything sits under root so it beats the
      // default size styles.
      root: {
        width: 44,
        height: 26,
        padding: 0,
        overflow: 'visible',
        flexShrink: 0,
        '& .MuiSwitch-switchBase': { padding: 3, color: '#FFFFFF', '&:hover': { backgroundColor: 'transparent' } },
        '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(18px)', color: '#FFFFFF', '&:hover': { backgroundColor: 'transparent' } },
        '& .MuiSwitch-thumb': { width: 20, height: 20, boxShadow: '0 1px 2px rgba(16, 24, 40, 0.24)' },
        '& .MuiSwitch-track': { borderRadius: 13, backgroundColor: TS.inputBorder, opacity: 1, transition: 'background-color 150ms ease' },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: TS.text, opacity: 1 },
        '& .MuiSwitch-switchBase.Mui-focusVisible + .MuiSwitch-track': focusRing
      }
    }
  },
  MuiButton: {
    styleOverrides: {
      ...base.components.MuiButton.styleOverrides,
      // Text buttons stay text buttons here (Back, Select All, Add another item).
      text: {
        backgroundColor: 'transparent',
        border: 'none',
        color: TS.text,
        '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.05)' },
        '&.Mui-disabled': { color: PM.disabledText, pointerEvents: 'auto', cursor: 'not-allowed' }
      },
      outlined: {
        ...base.components.MuiButton.styleOverrides.outlined,
        '&.Mui-disabled': { color: PM.disabledText, borderColor: TS.border, pointerEvents: 'auto', cursor: 'not-allowed' }
      },
      containedError: {
        backgroundColor: TS.danger,
        '&:hover': { backgroundColor: '#991B1B' }
      }
    }
  },
  MuiAlert: {
    styleOverrides: {
      ...base.components.MuiAlert.styleOverrides,
      standardWarning: {
        backgroundColor: PM.paleAmber,
        color: TS.amberText,
        border: `1px solid ${PM.noteBorder}`,
        '& .MuiAlert-icon': { color: TS.amberIcon }
      },
      standardInfo: {
        backgroundColor: TS.chipBg,
        color: PM.body,
        border: `1px solid ${TS.border}`,
        '& .MuiAlert-icon': { color: TS.secondary }
      }
    }
  }
})

export const promotionsTheme = outer => {
  const base = truckStopsTheme(outer)
  return createTheme({ ...base, components: { ...base.components, ...extra(base) } })
}
